// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

/**
 * D-UI-3 c5 — the one place a stored code becomes words (CareRx audit E11, E12, C9, C17, O16). Presentation
 * only, so it lives beside the UI kit, not in `api/`. A code with no entry is humanised ("in_progress" →
 * "In progress") rather than shown raw; a label is never guessed for a code that carries meaning.
 */
export function humanise(code: string | null | undefined): string {
  if (!code) return '—'
  const s = String(code).replace(/[_-]+/g, ' ').trim().toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Status words that read better than their humanised code. Shared by every domain. */
const STATUS: Record<string, string> = {
  na: 'N/A', in_progress: 'In progress', pending_callback: 'Pending callback', pa_approved: 'PA approved',
  pa_pending: 'PA pending', pa_denied: 'PA denied', not_started: 'Not started', in_review: 'In review',
  hard_hold: 'Blocks approval', soft_hold: 'Needs attention', advisory: 'Advisory',
}

/** Why a therapy is on hold — the screen used to label every non-lab hold "manual" (C9). */
const HOLD_SOURCE: Record<string, string> = {
  manual: 'Manual hold', screening_rule: 'Screening rule', lab_rule: 'Lab rule', determination: 'Conditional approval',
}

/** Rate models, in words a pharmacist reads (owner on the drug record: "I don't get it"). */
const RATE_MODEL: Record<string, string> = {
  not_applicable: 'Not applicable', fixed_duration: 'Fixed duration', mg_hr_ladder: 'Titrated — mg/hr',
  ml_hr_ladder: 'Titrated — mL/hr', mg_kg_min_ladder: 'Titrated by weight — mg/kg/min',
  ml_kg_min_ladder: 'Titrated by weight — mL/kg/min', sc_per_site: 'Subcutaneous — per site',
}

export type LabelDomain = 'status' | 'hold_source' | 'rate_model'
const DOMAINS: Record<LabelDomain, Record<string, string>> = { status: STATUS, hold_source: HOLD_SOURCE, rate_model: RATE_MODEL }

export function labelOf(code: string | null | undefined, domain: LabelDomain = 'status'): string {
  if (!code) return '—'
  const key = String(code).toLowerCase().replace(/[\s-]+/g, '_')
  return DOMAINS[domain][key] ?? STATUS[key] ?? humanise(code)
}
